import { useRef, useState } from 'react';
import { describeError, problemErrorCode, problemStatus, type StepUpProof } from './api';
import { describePasskeyFailure, hasActivePasskey, passkeyStepUpHeader, type PasskeyStepUpTarget } from './passkeys';

export { STEP_UP_HEADER, STEP_UP_PASSKEY_HEADER, stepUpHeaders, stepUpOptions, type StepUpProof } from './api';

/**
 * Step-up por TOTP do backoffice (change platform-backoffice-hardening, C6, tarefa 7.4).
 *
 * Desativar tenant, cancelar assinatura, aplicar preço às assinaturas de verdade e reembolsar
 * pacote de atendimentos (change billing-ai-units) pedem o código
 * atual do autenticador no cabeçalho `X-Platform-Step-Up`. A API confere o código antes da
 * `Idempotency-Key`: o 403 de step-up não reserva a chave, e a repetição depois do modal sai com a
 * mesma chave (`keepsIdempotencyKey`).
 *
 * Passkey (C14, tarefa 14.6): quando o navegador tem WebAuthn e a conta tem passkey ativa, o modal
 * oferece "Usar passkey" além do código; a passkey vale onde o TOTP vale, e o código continua
 * sendo a alternativa. A ação recebe a confirmação (`StepUpProof`): o código (texto) ou a asserção
 * já codificada (`{ passkey }`), que a chamada da API manda no cabeçalho certo (`stepUpOptions`).
 */

export const STEP_UP_REQUIRED = 'platform.step_up_required';
export const STEP_UP_INVALID = 'platform.step_up_invalid';
export const STEP_UP_RATE_LIMITED = 'platform.step_up_rate_limited';
/** O 423 da conta bloqueada por falhas de segundo fator, do login ou do step-up (C5). */
export const ACCOUNT_LOCKED = 'platform.auth.locked';
/** O 429 do freio de segundo fator (C5): as vagas de tentativa da conta estão tomadas. */
export const ATTEMPTS_IN_FLIGHT = 'platform.auth.attempts_in_flight';

/** Aviso quando o corpo confirmado deixou de valer até o envio do código (`isValid`). */
export const INVALID_FORM_MESSAGE = 'Revise o formulário: os dados confirmados não valem mais.';

/** O formato que a API aceita: 6 dígitos. Recovery code não cabe e nunca vale de step-up. */
const STEP_UP_CODE = /^\d{6}$/;

export function isStepUpCode(value: string): boolean {
  return STEP_UP_CODE.test(value);
}

/** 403 sem o cabeçalho (ou com ele vazio): a tela pede o código. */
export function isStepUpRequired(err: unknown): boolean {
  return problemStatus(err) === 403 && problemErrorCode(err) === STEP_UP_REQUIRED;
}

/** 403 do código que não confere, já usado, fora do formato ou de conta sem MFA concluído. */
export function isStepUpInvalid(err: unknown): boolean {
  return problemStatus(err) === 403 && problemErrorCode(err) === STEP_UP_INVALID;
}

/**
 * 429 do freio do step-up: o operador já mandou cinco códigos no último minuto. Recusado antes do
 * verificador e da `Idempotency-Key`; vem com `Retry-After`.
 */
export function isStepUpRateLimited(err: unknown): boolean {
  return problemStatus(err) === 429 && problemErrorCode(err) === STEP_UP_RATE_LIMITED;
}

/** 423 da conta bloqueada por tentativas erradas de segundo fator. */
export function isAccountLocked(err: unknown): boolean {
  return problemStatus(err) === 423 && problemErrorCode(err) === ACCOUNT_LOCKED;
}

/**
 * 429 do freio de segundo fator (C5): outra tentativa da conta está em andamento e não sobrou
 * vaga. Não é falha nem bloqueio; o operador tenta de novo em segundos.
 */
export function isAttemptsInFlight(err: unknown): boolean {
  return problemStatus(err) === 429 && problemErrorCode(err) === ATTEMPTS_IN_FLIGHT;
}

/** Recusa de step-up, dos freios ou de conta bloqueada: a API recusou antes da `Idempotency-Key`. */
export function isStepUpRefusal(err: unknown): boolean {
  return (
    isStepUpRequired(err) ||
    isStepUpInvalid(err) ||
    isStepUpRateLimited(err) ||
    isAttemptsInFlight(err) ||
    isAccountLocked(err)
  );
}

/**
 * Estado do `StepUpDialog` de uma ação sensível.
 *
 * `start(payload)` abre o modal e congela o corpo da ação: a tela só chama `start` com o formulário
 * válido (motivo e confirmação explícita), e `submit(code)` roda a ação com o código e esse corpo
 * congelado, nunca com o estado vivo da página. Enquanto o modal está aberto, a tela trava o
 * formulário de trás e o `StepUpDialog` prende o foco; mesmo assim, o que sai é o que o operador
 * confirmou ao abrir o modal. Um `start` com o modal já aberto é ignorado: não troca o corpo nem
 * apaga a recusa que o modal mostra.
 *
 * O 403 de step-up (código errado, já usado, ou o cabeçalho que não chegou) e o 429 do freio de
 * tentativas deixam o modal aberto com a mensagem, para o operador digitar o próximo código; a
 * ação repete com o mesmo corpo e a mesma `Idempotency-Key`, que a própria ação guarda
 * (`useIdempotencyKey`). Qualquer outro erro, inclusive o 423 da conta bloqueada, fecha o modal e
 * vai para `onFailure`, que a tela mostra no lugar dela.
 */
export function useStepUpDialog<T = void>(
  action: (proof: StepUpProof, payload: T) => Promise<void>,
  onFailure: (err: unknown) => void,
  options: {
    /**
     * Confere de novo, no envio do código, o corpo congelado (o `canSubmit` da tela sobre ele).
     * Falso fecha o modal sem chamar a ação e avisa por `onFailure`: o que sai é sempre um corpo
     * que a tela aceitaria.
     */
    isValid?: (payload: T) => boolean;
    /**
     * O alvo do desafio de passkey para o corpo congelado (propósito, tipo e id do alvo da ação,
     * C14). Sem ele, ou devolvendo `null`, o modal só oferece o código.
     */
    passkeyTarget?: (payload: T) => PasskeyStepUpTarget | null;
  } = {},
) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  // O corpo confirmado ao abrir o modal; `null` com o modal fechado.
  const frozen = useRef<{ payload: T } | null>(null);

  function start(payload: T) {
    if (frozen.current) return;
    frozen.current = { payload };
    setError(null);
    setPasskeyAvailable(false);
    setOpen(true);
    if (options.passkeyTarget?.(payload)) {
      void hasActivePasskey().then((available) => {
        if (frozen.current?.payload === payload) setPasskeyAvailable(available);
      });
    }
  }

  function close() {
    frozen.current = null;
    setOpen(false);
  }

  function cancel() {
    if (busy) return;
    close();
    setError(null);
  }

  async function submit(code: string) {
    await run(code);
  }

  /** "Usar passkey": desafio para o alvo, cerimônia do navegador e a ação com a asserção. */
  async function submitPasskey() {
    const confirmed = frozen.current;
    const target = confirmed ? options.passkeyTarget?.(confirmed.payload) : null;
    if (!confirmed || !target || busy) return;
    if (options.isValid && !options.isValid(confirmed.payload)) {
      close();
      setError(null);
      onFailure(new Error(INVALID_FORM_MESSAGE));
      return;
    }
    setBusy(true);
    setError(null);
    let header: string;
    try {
      header = await passkeyStepUpHeader(target);
    } catch (err) {
      // A cerimônia do navegador falhou ou foi cancelada: o modal continua, com o código como
      // alternativa. Recusa da API no pedido do desafio também fica no modal.
      setError(describePasskeyFailure(err) ?? describeError(err));
      setBusy(false);
      return;
    }
    setBusy(false);
    await run({ passkey: header });
  }

  async function run(proof: StepUpProof) {
    const confirmed = frozen.current;
    if (!confirmed || busy) return;
    if (options.isValid && !options.isValid(confirmed.payload)) {
      close();
      setError(null);
      onFailure(new Error(INVALID_FORM_MESSAGE));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await action(proof, confirmed.payload);
      close();
    } catch (err) {
      if (isStepUpInvalid(err) || isStepUpRequired(err) || isStepUpRateLimited(err) || isAttemptsInFlight(err)) {
        // O modal continua (ou volta a ficar) aberto: o operador digita o próximo código (no 429
        // do freio, depois de esperar o minuto; no das vagas tomadas, em segundos).
        setError(describeError(err));
        setOpen(true);
      } else {
        close();
        onFailure(err);
      }
    } finally {
      setBusy(false);
    }
  }

  return {
    open,
    busy,
    error,
    start,
    cancel,
    submit,
    /** O modal deve oferecer "Usar passkey" (navegador com WebAuthn e conta com passkey ativa). */
    passkeyAvailable,
    submitPasskey,
  };
}
